import { useRef } from 'react';
import {
  Box,
  SimpleGrid,
  FormLabel,
  HStack,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Select,
  Text,
  Tooltip,
  Checkbox,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiHelpCircle } from 'react-icons/fi';
import { KmlStyleOptions } from '../../utils/converters';

const PALETTE = [
  { color: '#DC2626', label: 'Red' },
  { color: '#EA580C', label: 'Orange' },
  { color: '#CA8A04', label: 'Yellow' },
  { color: '#16A34A', label: 'Green' },
  { color: '#0D9488', label: 'Teal' },
  { color: '#2563EB', label: 'Blue' },
  { color: '#7C3AED', label: 'Purple' },
  { color: '#DB2777', label: 'Pink' },
  { color: '#FFFFFF', label: 'White' },
  { color: '#000000', label: 'Black' },
] as const;

function OptionLabel({ children, tooltip }: { children: string; tooltip: string }) {
  return (
    <HStack spacing={1} mb={1} align="center">
      <FormLabel fontSize="xs" mb={0}>{children}</FormLabel>
      <Tooltip label={tooltip} placement="top" hasArrow fontSize="xs">
        <Box
          as="span"
          color="text.muted"
          cursor="help"
          display="inline-flex"
          alignItems="center"
          tabIndex={0}
        >
          <FiHelpCircle size={11} />
        </Box>
      </Tooltip>
    </HStack>
  );
}

interface ColorPickerProps {
  field: 'trackColor' | 'waypointColor';
  label: string;
  tooltip: string;
  value: string;
  onChange: (color: string) => void;
}

function ColorPicker({ field, label, tooltip, value, onChange }: ColorPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isCustom = !PALETTE.some((p) => p.color === value);

  return (
    <Box>
      <OptionLabel tooltip={tooltip}>{label}</OptionLabel>
      <HStack spacing="4px" flexWrap="wrap">
        {PALETTE.map(({ color, label: colorLabel }) => (
          <Box
            key={color}
            as="button"
            type="button"
            aria-label={colorLabel}
            w="22px"
            h="22px"
            borderRadius="4px"
            bg={color}
            border="none"
            outline="none"
            onClick={() => onChange(color)}
            sx={{
              outline: '2px solid',
              outlineColor: value === color ? 'var(--chakra-colors-brand-primary)' : 'transparent',
              outlineOffset: '2px',
              transition: 'outline-color 0.15s ease',
            }}
          />
        ))}
        <Box position="relative" display="inline-flex">
          <Box
            as="button"
            type="button"
            aria-label="Custom color"
            data-testid={`custom-color-${field}`}
            w="22px"
            h="22px"
            borderRadius="4px"
            bg={isCustom ? value : 'whiteAlpha.200'}
            border="none"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="14px"
            color="text.primary"
            outline="none"
            onClick={() => inputRef.current?.click()}
            sx={{
              outline: '2px solid',
              outlineColor: isCustom ? 'var(--chakra-colors-brand-primary)' : 'transparent',
              outlineOffset: '2px',
              transition: 'outline-color 0.15s ease',
            }}
          >
            {!isCustom && '+'}
          </Box>
          <input
            ref={inputRef}
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            tabIndex={-1}
            style={{ position: 'absolute', bottom: 0, left: 0, width: 0, height: 0, padding: 0, border: 'none', opacity: 0, pointerEvents: 'none' }}
            data-testid={`color-input-${field}`}
          />
        </Box>
      </HStack>
    </Box>
  );
}

interface StylePreviewProps {
  options: KmlStyleOptions;
}

function StylePreview({ options }: StylePreviewProps) {
  const strokeWidth = Math.max(1, options.trackWidth * 0.6);
  const clampedRadius = Math.min(14, Math.max(5, Math.round(8 * options.waypointScale)));
  const px = 148;
  const py = 28;
  const s = clampedRadius / 18;
  const tx = px - 32 * s;
  const ty = py - 22 * s;
  const previewBorderColor = useColorModeValue('border.subtle', 'whiteAlpha.100');

  return (
    <Box borderRadius="8px" bg="bg.surface.translucent" border="1px solid" borderColor={previewBorderColor} overflow="hidden" mb={3} aria-label="Style preview" role="img">
      <svg viewBox="0 0 300 72" width="100%" height="72" xmlns="http://www.w3.org/2000/svg">
        {options.includeTracks && (
          <path
            d="M 16,52 C 60,8 100,64 148,28 S 240,56 284,32"
            fill="none"
            stroke={options.trackColor}
            strokeWidth={strokeWidth}
            strokeOpacity={options.trackOpacity}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        {options.includeWaypoints && (
          <g transform={`translate(${tx},${ty}) scale(${s})`}>
            <path
              d="M 32 4 C 22.06 4, 14 12.06, 14 22 C 14 30, 19 36, 25 42 C 28 45, 31 50, 32 58 C 33 50, 36 45, 39 42 C 45 36, 50 30, 50 22 C 50 12.06, 41.94 4, 32 4 Z"
              fill={options.waypointColor}
              stroke="#000000"
              strokeWidth="2.5"
              strokeLinejoin="round"
            />
          </g>
        )}
      </svg>
    </Box>
  );
}

interface ConversionOptionsProps {
  options: KmlStyleOptions;
  onChange: <K extends keyof KmlStyleOptions>(key: K, value: KmlStyleOptions[K]) => void;
}

const dimmedStyle = { opacity: 0.35, pointerEvents: 'none' as const };

export default function ConversionOptions({ options, onChange }: ConversionOptionsProps) {
  const panelBorderColor = useColorModeValue('border.subtle', 'whiteAlpha.200');
  const isWaypointDimmed = !options.includeWaypoints;
  const isLineDimmed = !options.includeTracks && !options.includeRoutes;

  return (
    <Box
      borderRadius="12px"
      border="1px solid"
      borderColor={panelBorderColor}
      p={4}
      mt={2}
      data-testid="conversion-options-panel"
    >
      <Box mb={3}>
        <Text
          fontSize="xs"
          fontWeight="semibold"
          letterSpacing="0.08em"
          textTransform="uppercase"
          color="text.muted"
          mb={2}
        >
          Include in KML
        </Text>
        <HStack spacing={5}>
          <Checkbox
            isChecked={options.includeWaypoints}
            onChange={(e) => onChange('includeWaypoints', e.target.checked)}
            colorScheme="orange"
            size="sm"
          >
            Waypoints
          </Checkbox>
          <Checkbox
            isChecked={options.includeTracks}
            onChange={(e) => onChange('includeTracks', e.target.checked)}
            colorScheme="orange"
            size="sm"
          >
            Track
          </Checkbox>
          <Checkbox
            isChecked={options.includeRoutes}
            onChange={(e) => onChange('includeRoutes', e.target.checked)}
            colorScheme="orange"
            size="sm"
          >
            Routes
          </Checkbox>
        </HStack>
      </Box>

      <Divider borderColor={panelBorderColor} mb={4} />

      <StylePreview options={options} />

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <Box data-testid="line-color-control" style={isLineDimmed ? dimmedStyle : {}}>
          <ColorPicker
            field="trackColor"
            label="Line Color"
            tooltip="Color of track and route lines in KML viewers like Google Earth"
            value={options.trackColor}
            onChange={(hex) => onChange('trackColor', hex)}
          />
        </Box>

        <Box data-testid="waypoint-color-control" style={isWaypointDimmed ? dimmedStyle : {}}>
          <ColorPicker
            field="waypointColor"
            label="Waypoint Color"
            tooltip="Color of waypoint markers in KML viewers like Google Earth"
            value={options.waypointColor}
            onChange={(hex) => onChange('waypointColor', hex)}
          />
        </Box>

        <Box data-testid="line-width-control" style={isLineDimmed ? dimmedStyle : {}}>
          <OptionLabel tooltip="Thickness of the track line in pixels">Line Width</OptionLabel>
          <Slider
            aria-label="Line width"
            colorScheme="orange"
            min={1}
            max={10}
            step={1}
            value={options.trackWidth}
            onChange={(v) => onChange('trackWidth', v)}
          >
            <SliderTrack><SliderFilledTrack /></SliderTrack>
            <SliderThumb />
          </Slider>
          <Text fontSize="xs" color="text.muted" mt={1}>{options.trackWidth}px</Text>
        </Box>

        <Box data-testid="waypoint-size-control" style={isWaypointDimmed ? dimmedStyle : {}}>
          <OptionLabel tooltip="Scale of waypoint marker icons — 1.0× is the default size">Waypoint Size</OptionLabel>
          <Slider
            aria-label="Waypoint size"
            colorScheme="orange"
            min={0.5}
            max={2.0}
            step={0.1}
            value={options.waypointScale}
            onChange={(v) => onChange('waypointScale', v)}
          >
            <SliderTrack><SliderFilledTrack /></SliderTrack>
            <SliderThumb />
          </Slider>
          <Text fontSize="xs" color="text.muted" mt={1}>{options.waypointScale.toFixed(1)}×</Text>
        </Box>

        <Box data-testid="line-opacity-control" style={isLineDimmed ? dimmedStyle : {}}>
          <OptionLabel tooltip="How transparent the track line is — 100% is fully visible, lower values make it semi-transparent">Line Opacity</OptionLabel>
          <Slider
            aria-label="Line opacity"
            colorScheme="orange"
            min={0.1}
            max={1.0}
            step={0.1}
            value={options.trackOpacity}
            onChange={(v) => onChange('trackOpacity', v)}
          >
            <SliderTrack><SliderFilledTrack /></SliderTrack>
            <SliderThumb />
          </Slider>
          <Text fontSize="xs" color="text.muted" mt={1}>{Math.round(options.trackOpacity * 100)}%</Text>
        </Box>

        <Box>
          <OptionLabel tooltip="How KML viewers interpret altitude — 'Follow ground' drapes the track on terrain regardless of elevation data">Altitude Mode</OptionLabel>
          <Select
            size="sm"
            value={options.altitudeMode}
            onChange={(e) => onChange('altitudeMode', e.target.value as KmlStyleOptions['altitudeMode'])}
          >
            <option value="clampToGround">Follow ground</option>
            <option value="relativeToGround">Relative to ground</option>
            <option value="absolute">Absolute altitude</option>
          </Select>
        </Box>
      </SimpleGrid>
    </Box>
  );
}
